import { UseQueryOptions } from '@tanstack/react-query';
import {
	ExecutionHistory,
	ExecutionHistoryGetLogsPayload,
	ExecutionHistoryGetObjectsPayload,
	ExecutionHistoryPayload,
	ExecutionHistoryStub,
	ExecutionLog,
	getByExecutionIDPath,
	getExecutionHistoryPayloadsPath,
	getLogsForTaskPath,
	getPageByFlowIDPath,
	ItemPage,
	PayloadTypes,
	urlCombine,
} from 'ihub/common';
import {
	coreAPI,
	getTenantID,
	isURLSet,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import { cacheStaleTime } from '../constants';
import QueryClientWrapper from '../utils/create-query-client';
import Parameter from './../classes/query-parameter';

export class ExecutionHistoryHttpService {
	getPageByFlowID(
		flowID: string,
		limit: number,
		offset: number,
		queryParams?: Parameter[]
	): UseQueryOptions<ItemPage<ExecutionHistoryStub>, Error, ItemPage<ExecutionHistoryStub>> {
		return {
			queryKey: [
				getTenantID(),
				ExecutionHistoryStub.name,
				getPageByFlowIDPath,
				flowID,
				limit,
				offset,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<ItemPage<ExecutionHistoryStub>> => {
				isURLSet();
				let url = urlCombine(
					coreAPI,
					ExecutionHistory.name,
					getPageByFlowIDPath,
					flowID,
					limit.toString(),
					offset.toString()
				);
				if (queryParams && queryParams.length > 0) {
					url += `?` + queryParams.map(x => `${x.key}=${x.value}`).join('&');
				}
				const res = await standardGet(url);
				await validateOkResponse(res, 'get page by ID', ExecutionHistory.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getByExecutionID(
		executionID: string
	): UseQueryOptions<ExecutionHistory, Error, ExecutionHistory> {
		return {
			queryKey: [getTenantID(), ExecutionHistory.name, getByExecutionIDPath, executionID],
			queryFn: async (): Promise<ExecutionHistory> => {
				isURLSet();
				const url = urlCombine(coreAPI, ExecutionHistory.name, getByExecutionIDPath, executionID);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get by ID', ExecutionHistory.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getLogsForTask(
		executionHistoryGetLogsPayload: ExecutionHistoryGetLogsPayload
	): UseQueryOptions<ExecutionLog[], Error, ExecutionLog[]> {
		return {
			queryKey: [
				getTenantID(),
				ExecutionLog.name,
				getLogsForTaskPath,
				executionHistoryGetLogsPayload.taskID,
				executionHistoryGetLogsPayload.subTaskID ?? '',
				executionHistoryGetLogsPayload.executionID,
			],
			queryFn: async (): Promise<ExecutionLog[]> => {
				isURLSet();
				const url = urlCombine(coreAPI, ExecutionHistory.name, getLogsForTaskPath);
				const res = await standardPost(url, executionHistoryGetLogsPayload);
				await validateOkResponse(res, 'get logs for task', ExecutionHistory.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getExecutionHistoryPayloads(
		executionHistoryGetObjectsPayload: ExecutionHistoryGetObjectsPayload
	): UseQueryOptions<ItemPage<ExecutionHistoryPayload>, Error, ItemPage<ExecutionHistoryPayload>> {
		return {
			queryKey: [
				getTenantID(),
				ExecutionHistory.name,
				getExecutionHistoryPayloadsPath,
				PayloadTypes[executionHistoryGetObjectsPayload.payloadType],
				executionHistoryGetObjectsPayload.taskID,
				executionHistoryGetObjectsPayload.subTaskID ?? '',
				JSON.stringify(executionHistoryGetObjectsPayload.advancedFilters) ?? '',
				executionHistoryGetObjectsPayload.limit.toString(),
				executionHistoryGetObjectsPayload.offset.toString(),
			],
			queryFn: async (): Promise<ItemPage<ExecutionHistoryPayload>> => {
				isURLSet();
				const url = urlCombine(coreAPI, ExecutionHistory.name, getExecutionHistoryPayloadsPath);
				const res = await standardPost(url, executionHistoryGetObjectsPayload);
				await validateOkResponse(res, 'get Execution History payloads', ExecutionHistory.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async invalidateKeys(flowID?: string, remove = false) {
		if (flowID) {
			await QueryClientWrapper.InvalidateKey(
				[getTenantID(), ExecutionHistoryStub.name, getPageByFlowIDPath, flowID],
				remove
			);
		} else {
			await QueryClientWrapper.InvalidateKey(
				[getTenantID(), ExecutionHistoryStub.name, getPageByFlowIDPath],
				remove
			);
		}
	}

	async invalidateExecutionHistory(payloadTypes?: PayloadTypes, taskID?: string, remove = false) {
		const key: unknown[] = [getTenantID(), ExecutionHistory.name, getExecutionHistoryPayloadsPath];
		if (payloadTypes !== undefined) {
			key.push(PayloadTypes[payloadTypes]);
		}
		if (taskID) {
			key.push(taskID);
		}
		await QueryClientWrapper.InvalidateKey(key, remove);
	}
}
